import React, { useState, useContext, useMemo, useRef, useLayoutEffect, useEffect } from 'react'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import UserContext from '../context/UserContext'
import languages, { getLanguageAcronym } from '../languages'
import useLearnerContext from '../hooks/useLearnerContext'
import { isOwnerRole } from '../util/roles'
import {
  GET_LANGUAGE_TREE_QUERY, SEED_CURRICULUM_MUTATION, COMPLETE_TOPIC_MUTATION,
  ATTACH_LESSON_TO_TOPIC_MUTATION, ADD_MEETING_WITH_CONTENT_MUTATION, GET_MEETING_WITH_CONTENT_QUERY, ME_QUERY,
} from '../query/gqlQueries'
import { streamOllamaGenerate } from '../ollama'
import StudentLessonView from './StudentLessonView'
import curricula from '../data/curricula'
import { getBuiltinLesson } from '../data/lessons'

import CloseIcon from '@mui/icons-material/Close'
import LockIcon from '@mui/icons-material/Lock'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const LEVEL_NAMES = { A1: 'Beginner', A2: 'Elementary', B1: 'Intermediate', B2: 'Upper Int.', C1: 'Advanced', C2: 'Mastery' }
const S = (v) => String(v || '').trim()
const parseObj = (raw) => {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = s.indexOf('{'), b = s.lastIndexOf('}')
  if (a >= 0 && b > a) s = s.slice(a, b + 1)
  try { return JSON.parse(s) } catch { return null }
}
const pairs = (arr) => (Array.isArray(arr) ? arr : []).map(x => ({ tr: S(x?.tr), en: S(x?.en) })).filter(p => p.tr)

// Normalise a generated lesson into the shape StudentLessonView renders.
const cleanLesson = (o, code, name, level) => {
  if (!o) return null
  const sections = (Array.isArray(o.sections) ? o.sections : []).map(sec => {
    const ex = sec?.exercise
    const items = Array.isArray(ex?.items) ? ex.items.map(it => ({ q: S(it?.q), a: S(it?.a), options: Array.isArray(it?.options) ? it.options.map(S).filter(Boolean) : undefined })).filter(it => it.q || it.a) : []
    // Paradigm table: columns + rows of strings (rendered by StudentLessonView).
    const tb = sec?.table
    const table = tb && Array.isArray(tb.rows) && tb.rows.length
      ? { columns: (Array.isArray(tb.columns) ? tb.columns : []).map(S), rows: tb.rows.map(r => (Array.isArray(r) ? r : []).map(S)).filter(r => r.length) }
      : undefined
    return {
      kind: ['concept', 'vocab', 'paradigm', 'patterns', 'practice'].includes(sec?.kind) ? sec.kind : (items.length ? 'practice' : 'concept'),
      title: S(sec?.title) || 'Section', note: S(sec?.note) || undefined, table,
      words: pairs(sec?.words), sentences: pairs(sec?.sentences),
      exercise: items.length ? { type: S(ex?.type) || 'translate', prompt: S(ex?.prompt), items } : undefined,
    }
  }).filter(s => s.words.length || s.sentences.length || s.exercise || s.note || s.table)
  const r = o.reading
  const reading = S(r?.text) ? { title: S(r?.title) || 'Reading', text: S(r.text), glossary: pairs(r?.glossary), questions: (Array.isArray(r?.questions) ? r.questions : []).map(q => ({ q: S(q?.q), a: S(q?.a) })).filter(q => q.q) } : undefined
  const d = o.dialogue
  const dLines = (Array.isArray(d?.lines) ? d.lines : []).map(l => ({ who: S(l?.who), tr: S(l?.tr), en: S(l?.en) })).filter(l => l.tr)
  const dialogue = dLines.length ? { title: S(d?.title) || 'Mini dialogue', lines: dLines } : undefined
  const speaking = (Array.isArray(o.speaking) ? o.speaking : []).map(S).filter(Boolean)
  if (sections.length === 0 && !reading && !dialogue) return null
  return { title: S(o.title) || 'Lesson', level: S(o.level) || level, goal: S(o.goal), language: code, languageName: name, sections, reading, dialogue, speaking: speaking.length ? speaking : undefined }
}

// initialLanguage / initialSlug: the dashboard opens the tree on a language and
// (optionally) straight onto one topic's modal.
export default function LanguageTreeView({ voiceSpeed, onClose, initialLanguage, initialSlug }) {
  const { username } = useContext(UserContext)
  const { learning, personalize } = useLearnerContext(username)
  const personalKey = (t) => `lt-personal-${code}-${t.slug}`
  const readPersonal = (t) => { try { return localStorage.getItem(personalKey(t)) } catch { return null } }
  const learningNames = (learning || []).map(l => l.name).filter(Boolean)

  const [langName, setLangName] = useState(initialLanguage || '')
  const effName = langName || learningNames[0] || 'Turkish'
  const code = getLanguageAcronym(effName) || 'tur'

  // errorPolicy 'all' (not 'ignore'): a failing query must surface, otherwise a
  // backend that hasn't shipped the Topic schema looks like an empty tree.
  const { data, loading, refetch, error: treeError } = useQuery(GET_LANGUAGE_TREE_QUERY, {
    variables: { username, langCode: code }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'all',
  })
  const topics = data?.getLanguageTree || []
  const backendOutOfDate = !!treeError && /Cannot query field|Unknown type/i.test(treeError.message || '')
  // Only the owner may author / reseed the shared tree; students just climb it.
  const { data: meData } = useQuery(ME_QUERY, { errorPolicy: 'ignore' })
  const isOwner = isOwnerRole(meData?.me?.role)

  const [seedCurriculum] = useMutation(SEED_CURRICULUM_MUTATION)
  const [completeTopic] = useMutation(COMPLETE_TOPIC_MUTATION)
  const [attachLesson] = useMutation(ATTACH_LESSON_TO_TOPIC_MUTATION)
  const [addMeeting] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)
  const [fetchMeeting] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY, { fetchPolicy: 'network-only' })

  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(null)   // topic node in the modal
  const [lesson, setLesson] = useState(null)       // { lesson, meetingId } → StudentLessonView

  // Opened from the dashboard on a specific topic: pop its modal once the tree
  // has loaded — once only, so closing the modal doesn't reopen it.
  const focusedSlugRef = useRef(null)
  useEffect(() => {
    if (!initialSlug || focusedSlugRef.current === initialSlug || !topics.length) return
    const t = topics.find(x => x.slug === initialSlug)
    if (t) { focusedSlugRef.current = initialSlug; setSelected(t) }
  }, [initialSlug, topics])

  const completedSlugs = useMemo(() => new Set(topics.filter(t => t.completed).map(t => t.slug)), [topics])
  const stateOf = (t) => t.completed ? 'done' : ((t.requires || []).every(r => completedSlugs.has(r)) ? 'open' : 'locked')
  const titleBySlug = useMemo(() => Object.fromEntries(topics.map(t => [t.slug, t.title])), [topics])

  // Hover a card → light up only its prerequisite chain and its unlocks, fade
  // the rest. Keeps a dense tree calm instead of drawing every edge at once.
  const [hovered, setHovered] = useState(null)
  const dependents = useMemo(() => {
    const m = {}
    topics.forEach(t => (t.requires || []).forEach(r => { (m[r] = m[r] || []).push(t.slug) }))
    return m
  }, [topics])
  const neighborSet = useMemo(() => {
    if (!hovered) return null
    const t = topics.find(x => x.slug === hovered)
    const s = new Set([hovered])
    ;(t?.requires || []).forEach(r => s.add(r))
    ;(dependents[hovered] || []).forEach(d => s.add(d))
    return s
  }, [hovered, topics, dependents])

  const byLevel = useMemo(() => {
    const m = {}
    topics.forEach(t => { const lv = t.level || 'A1'; (m[lv] = m[lv] || []).push(t) })
    Object.values(m).forEach(a => a.sort((x, y) => (x.order || 0) - (y.order || 0)))
    return m
  }, [topics])
  const levelsPresent = LEVELS.filter(l => byLevel[l]?.length)

  // ── prerequisite arrows: measured after layout, drawn in an SVG overlay ─────
  const treeRef = useRef(null)
  const nodeRefs = useRef({})
  const [links, setLinks] = useState([])
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const compute = () => {
      const wrap = treeRef.current
      if (!wrap) return
      const wr = wrap.getBoundingClientRect()
      const out = []
      topics.forEach(t => (t.requires || []).forEach(r => {
        const a = nodeRefs.current[r], b = nodeRefs.current[t.slug]
        if (!a || !b) return
        const ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect()
        const sameCol = Math.abs(ra.left - rb.left) < 10
        const x1 = sameCol ? ra.left - wr.left + ra.width / 2 : ra.right - wr.left
        const y1 = sameCol ? ra.bottom - wr.top : ra.top - wr.top + ra.height / 2
        const x2 = sameCol ? rb.left - wr.left + rb.width / 2 : rb.left - wr.left
        const y2 = sameCol ? rb.top - wr.top : rb.top - wr.top + rb.height / 2
        const locked = !(t.requires || []).every(q => completedSlugs.has(q))
        out.push({ key: `${r}>${t.slug}`, from: r, to: t.slug, x1, y1, x2, y2, sameCol, done: completedSlugs.has(r), locked })
      }))
      setLinks(out)
      setSvgSize({ w: wrap.scrollWidth, h: wrap.scrollHeight })
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [topics, completedSlugs])

  const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  // ── AI-seed the curriculum for this language ─────────────────────────────────
  const generateCurriculum = async () => {
    setError('')
    // Prefer the hand-authored canonical tree (src/data/curricula.js); fall back
    // to the local model only for a language we haven't written one for.
    const builtIn = curricula[code]
    if (builtIn) {
      setBusy('Loading the curriculum…')
      try {
        await seedCurriculum({ variables: { langCode: code, name: builtIn.name || effName, topics: builtIn.topics } })
        await refetch()
      } catch (e) { setError(e.message || 'Could not load the curriculum.') }
      setBusy('')
      return
    }
    setBusy('Designing the tree…')
    const prompt = `Design a beginner→intermediate SKILL TREE for learning ${effName}, like a game tech tree. Produce 16-22 topics across CEFR A1, A2 and B1. Each topic is ONE concrete grammar or vocabulary unit a student unlocks in order.
For each: slug (kebab-case unique id), title (short), level ("A1"|"A2"|"B1"), order (1-based within its level), blurb (one short line), requires (array of slugs that MUST be done first — earlier topics only; the very first topics have []).
Prerequisites must reference ONLY slugs present in your list and form a sensible progression (basics before advanced).
Return ONLY minified JSON: {"topics":[{"slug":"","title":"","level":"","order":1,"blurb":"","requires":[]}]}`
    try {
      const raw = await streamOllamaGenerate({
        serverUrl, apiKey, body: { model: 'qwen2.5:14b', prompt, format: 'json', options: { temperature: 0.4, num_predict: 2600 } },
        onProgress: (acc) => setBusy(`Designing the tree… (${acc.length} chars)`),
      })
      const o = parseObj(raw)
      const list = (Array.isArray(o?.topics) ? o.topics : []).map(t => ({
        slug: S(t?.slug).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        title: S(t?.title), level: LEVELS.includes(t?.level) ? t.level : 'A1',
        order: Number.isFinite(t?.order) ? t.order : 0, blurb: S(t?.blurb),
        requires: Array.isArray(t?.requires) ? t.requires.map(s => S(s).toLowerCase().replace(/[^a-z0-9]+/g, '-')) : [],
      })).filter(t => t.slug && t.title)
      const slugs = new Set(list.map(t => t.slug))
      list.forEach(t => { t.requires = t.requires.filter(r => slugs.has(r) && r !== t.slug) }) // drop dangling prereqs
      if (list.length === 0) { setError('The model returned no topics — try again.'); setBusy(''); return }
      await seedCurriculum({ variables: { langCode: code, name: effName, topics: list } })
      await refetch()
    } catch (e) {
      setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
    setBusy('')
  }

  // Prompt for a topic lesson. `personal` is null for the shared/generic
  // (canonical) lesson, or a personalisation brief for a private one.
  // Workbook-style prompt (the structure of a good teacher's handout). When a
  // hand-authored canonical lesson exists it is passed as `exemplar` so the
  // model ADAPTS a correct lesson instead of inventing one from scratch.
  const lessonPrompt = (t, personal, exemplar) => {
    const level = t.level || 'A1'
    const audience = personal
      ? `for a specific learner — weave THEIR real details into the examples naturally where it fits (use their name, city, interests). Do NOT invent facts about them. ${personal}`
      : `for an English speaker learning ${effName}. Use neutral, generic examples — do NOT reference any specific real person, name, city, job or hobby; use common textbook names/places if a name is needed.`
    const style = exemplar
      ? `\nHere is the CANONICAL lesson for this topic. Keep its structure, grammar explanations, tables, section order and difficulty; only adapt example sentences, vocabulary contexts, the reading and the dialogue to the learner. Every grammar fact must stay exactly correct.\n${JSON.stringify(exemplar)}\n`
      : ''
    return `Create a ${effName} workbook lesson for the topic "${t.title}" (CEFR ${level}) ${audience}
${style}
Write it like a good teacher's workbook, in this order (5-7 sections):
1) "concept": a 1-2 sentence "note" explaining the grammar point in plain English, then 4-6 example "sentences" IN ${effName} with English.
2) "paradigm": a "table" showing the forms — "columns" (e.g. Pronoun | ${effName} | English) and 4-6 "rows"; put vowel-harmony / spelling rules in "note".
3) "vocab": 8-12 useful ${effName} "words" for the topic with English (recycle common beginner words).
4) "patterns": 4-8 model "sentences" IN ${effName} with English that combine the grammar with the vocab.
5) two or three "practice" sections, each with ONE "exercise" where the learner must answer IN ${effName}:
   - "fill": q = a ${effName} sentence with exactly one blank ____ (English hint in "prompt"); a = the missing ${effName} word.
   - "choose": q = a short question; "options" = 3 plausible ${effName} choices; a = the correct option (one of options).
   - "translate": q = a short English sentence; a = its ${effName} translation.
   - "match": items pair q (${effName}) with a (English).
Then "reading": a 6-10 sentence ${effName} passage that REUSES the lesson vocabulary, a "glossary" of 3-5 new words, and 3-4 "questions" whose answers "a" are SHORT ${effName} phrases from the text.
Then "dialogue": ONE coherent mini-conversation between two people (A and B) in a concrete everyday situation (e.g. at a café, meeting a friend, at school). 6-8 short lines. Every line must respond to the line before it — a question is answered in the next line, an answer is followed up — so it reads like a real exchange with a beginning and an end, NOT a list of unrelated example sentences. Reuse the lesson vocabulary and the grammar point.
Then "speaking": 3 prompts in English asking the learner to say something about themselves using the grammar.

STRICT: every "tr", every exercise "a", every "options" entry, every ${effName}-column table cell, and every reading answer "a" MUST be in ${effName}. English appears ONLY in "en", "note", "prompt", translate/choose question "q", and speaking prompts. True to CEFR ${level}. All ${effName} must be grammatically correct.
Return ONLY minified JSON: {"title":"","level":"${level}","goal":"","sections":[{"kind":"concept|paradigm|vocab|patterns|practice","title":"","note":"","table":{"columns":[],"rows":[[]]},"words":[{"tr":"","en":""}],"sentences":[{"tr":"","en":""}],"exercise":{"type":"fill|choose|translate|match","prompt":"","items":[{"q":"","a":"","options":[]}]}}],"reading":{"title":"","text":"","glossary":[{"tr":"","en":""}],"questions":[{"q":"","a":""}]},"dialogue":{"title":"","lines":[{"who":"","tr":"","en":""}]},"speaking":[""]}`
  }

  // Generate + save a lesson meeting. Returns { lessonObj, meetingId }.
  // `prebuilt` = a hand-authored lesson (src/data/lessons) → saved as-is, no
  // model call. Otherwise the model writes one; if an authored lesson exists it
  // is handed over as the exemplar so a personalised copy keeps its quality.
  const buildLessonMeeting = async (t, personal, prebuilt) => {
    const level = t.level || 'A1'
    let lessonObj
    if (prebuilt) {
      lessonObj = cleanLesson(prebuilt, code, effName, level)
    } else {
      const raw = await streamOllamaGenerate({
        serverUrl, apiKey, body: { model: 'gemma3:27b', prompt: lessonPrompt(t, personal, getBuiltinLesson(code, t.slug)), format: 'json', options: { temperature: 0.4, num_predict: 4500 } },
        onProgress: (acc) => setBusy(`${personal ? 'Tailoring' : 'Building'} the lesson… (${acc.length} chars)`),
      })
      lessonObj = cleanLesson(parseObj(raw), code, effName, level)
    }
    if (!lessonObj) throw new Error('Could not build a lesson — try again.')
    const words = []; const sentences = []
    lessonObj.sections.forEach(s => {
      (s.words || []).forEach(w => words.push({ word: w.tr, translation: w.en, lang: code }))
      ;(s.sentences || []).forEach(se => sentences.push({ sourceText: se.tr, targetText: se.en, sourceLang: code, targetLang: 'eng' }))
    })
    const saved = await addMeeting({
      variables: { input: {
        meeting: { title: `${t.title}${personal ? ' (personal)' : ''} — ${effName}`, type: 'Language Class', description: `${level} · ${lessonObj.goal || t.blurb || ''}`.slice(0, 240), meetingDate: new Date().toISOString().split('T')[0], username, lessonData: JSON.stringify(lessonObj) },
        sentences, words, tasks: [],
      } },
    })
    return { lessonObj, meetingId: saved.data?.addMeetingWithContent?.meeting?.identity }
  }

  const openSavedMeeting = async (meetingId, t, msg) => {
    setBusy(msg || 'Loading lesson…')
    try {
      const res = await fetchMeeting({ variables: { identity: meetingId } })
      const parsed = parseObj(res.data?.getMeetingWithContent?.meeting?.properties?.lessonData)
      if (parsed) { setSelected(null); setLesson({ lesson: parsed, meetingId, topicId: t.id, completed: t.completed }); return true }
    } catch (e) { setError(e.message) }
    setBusy('')
    return false
  }

  // Open the shared canonical lesson. `force` (owner) rebuilds a fresh generic
  // one and REPLACES the attached lesson — used to refresh / de-personalise.
  const openTopic = async (t, force = false) => {
    setError('')
    if (!force && t.lessonMeetingId) {
      if (await openSavedMeeting(t.lessonMeetingId, t)) return
      setError('This lesson has no content — an owner can regenerate it.'); setBusy(''); return
    }
    // Prefer the hand-authored canonical lesson (instant, correct, offline);
    // fall back to the local model only for topics nobody has written yet.
    const builtin = getBuiltinLesson(code, t.slug)
    setBusy(builtin ? 'Loading the lesson…' : (force ? 'Rebuilding the shared lesson…' : 'Building the lesson…'))
    try {
      const { lessonObj, meetingId } = await buildLessonMeeting(t, null, builtin)   // generic, shared
      if (meetingId) { await attachLesson({ variables: { topicId: t.id, meetingId } }); await refetch() }
      setSelected(null); setLesson({ lesson: lessonObj, meetingId, topicId: t.id, completed: t.completed })
    } catch (e) {
      setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
    setBusy('')
  }

  // A private, tailored copy — generated with the learner's profile, saved as
  // THEIR meeting, remembered locally, and NEVER attached to the shared topic.
  const personaliseTopic = async (t) => {
    setError(''); setBusy('Tailoring your lesson…')
    try {
      const { lessonObj, meetingId } = await buildLessonMeeting(t, personalize(effName) || 'The learner has no profile details yet — keep it generic but friendly.')
      if (meetingId) { try { localStorage.setItem(personalKey(t), meetingId) } catch { /* private */ } }
      setSelected(null); setLesson({ lesson: lessonObj, meetingId, topicId: t.id, completed: t.completed })
    } catch (e) {
      setError((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
    setBusy('')
  }
  const openMyPersonal = async (t) => {
    const id = readPersonal(t)
    if (!id) return personaliseTopic(t)
    setError('')
    const ok = await openSavedMeeting(id, t, 'Loading your version…')
    if (!ok) { try { localStorage.removeItem(personalKey(t)) } catch { /* */ }; personaliseTopic(t) }
  }

  const markComplete = async (t) => {
    try { await completeTopic({ variables: { username, topicId: t.id } }); await refetch(); setSelected(null) }
    catch (e) { setError(e.message) }
  }

  // ── the opened lesson replaces the tree ──────────────────────────────────────
  if (lesson) {
    const finishTopic = async () => {
      if (lesson.topicId && !lesson.completed) { try { await completeTopic({ variables: { username, topicId: lesson.topicId } }) } catch (e) { /* non-fatal */ } }
      setLesson(null); refetch()
    }
    return <StudentLessonView lesson={lesson.lesson} meetingId={lesson.meetingId} voiceSpeed={voiceSpeed}
      onComplete={lesson.topicId ? finishTopic : undefined} completed={lesson.completed}
      onClose={() => { setLesson(null); refetch() }} />
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'radial-gradient(1200px 600px at 50% -10%, #16161c, #0b0b0d)', color: '#e8e8ec', display: 'flex', flexDirection: 'column', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, zIndex: 5, background: '#23232b', border: '1px solid #2a2a33', color: '#e8e8ec', borderRadius: 10, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><CloseIcon /></button>

      <header style={{ padding: '1.3rem 1.6rem 0.8rem' }}>
        <div style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8a95' }}>Skill Tree</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700 }}>Your {effName} tree</h1>
          <select value={effName} onChange={e => setLangName(e.target.value)} style={{ background: '#141418', border: '1px solid #2a2a33', color: '#e8e8ec', borderRadius: 8, padding: '0.4rem 0.7rem' }}>
            {(learningNames.length ? learningNames : Object.values(languages)).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ color: '#8a8a95', fontSize: '0.85rem' }}>{topics.filter(t => t.completed).length} / {topics.length} done</span>
        </div>
      </header>

      {error && <div style={{ color: '#f87171', padding: '0 1.6rem 0.5rem' }}>{error}</div>}
      {busy && <div style={{ color: '#6ee7b7', padding: '0 1.6rem 0.5rem', fontSize: '0.9rem' }}>{busy}</div>}

      <main style={{ flex: 1, overflow: 'auto', padding: '0.6rem 2.2rem 2.5rem' }}>
        {loading && topics.length === 0 ? (
          <div style={{ color: '#8a8a95', padding: '2rem' }}>Loading…</div>
        ) : backendOutOfDate ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', maxWidth: 520, margin: '0 auto' }}>
            <div style={{ fontSize: '2.4rem' }}>⚠️</div>
            <h2 style={{ margin: '0.6rem 0' }}>The server hasn't been updated yet</h2>
            <p style={{ color: '#8a8a95' }}>The GraphQL server is running a version without the Skill Tree schema. Redeploy / restart it, then reopen this view.</p>
            <code style={{ color: '#f87171', fontSize: '0.8rem' }}>{treeError.message}</code>
          </div>
        ) : topics.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', maxWidth: 460, margin: '0 auto' }}>
            <div style={{ fontSize: '2.4rem' }}>🌱</div>
            <h2 style={{ margin: '0.6rem 0' }}>No tree yet for {effName}</h2>
            {isOwner ? (
              <>
                <p style={{ color: '#8a8a95' }}>{curricula[code]
                  ? 'Load the canonical curriculum — the shared tree every learner of this language climbs. You can refine it afterwards.'
                  : 'No hand-written tree for this language yet — generate a starter with the local model, then refine it.'}</p>
                <button onClick={generateCurriculum} disabled={!!busy} style={{ marginTop: '1rem', background: '#34d399', color: '#06281d', border: 'none', borderRadius: 10, padding: '0.7rem 1.5rem', fontWeight: 600, cursor: 'pointer' }}>
                  {curricula[code] ? '✦ Load curriculum' : '✦ Generate with AI'}
                </button>
              </>
            ) : (
              <p style={{ color: '#8a8a95' }}>Your teacher hasn't set up this tree yet — check back soon.</p>
            )}
          </div>
        ) : (
          <div ref={treeRef} className="lt-tree">
            <style>{`
              .lt-tree { position: relative; display: flex; gap: 11rem; align-items: flex-start; min-width: min-content; padding-top: 0.2rem; }
              .lt-col { flex: 0 0 auto; width: 268px; position: relative; }
              .lt-col + .lt-col::before { content: ''; position: absolute; left: -5.5rem; top: 3rem; bottom: 0; width: 1px; background: linear-gradient(180deg, transparent, rgba(42,42,51,0.55) 8%, rgba(42,42,51,0.55) 92%, transparent); }
              .lt-era { position: sticky; top: 0; z-index: 3; display: flex; align-items: baseline; gap: 0.55rem; padding: 0.4rem 0.1rem 0.6rem; margin-bottom: 1.4rem; background: linear-gradient(180deg, #0b0b0d 55%, rgba(11,11,13,0.7) 85%, transparent); backdrop-filter: blur(6px); }
              .lt-era b { font-size: 1.05rem; font-weight: 800; letter-spacing: 0.12em; color: #6ee7b7; }
              .lt-era span { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.1em; color: #5a5a65; }
              .lt-track { display: flex; flex-direction: column; gap: 3rem; }
              /* opaque base so prerequisite lines pass BEHIND cards and only show in the lanes */
              .lt-node { text-align: left; width: 100%; background: #111116; border: 1px solid; border-radius: 14px; padding: 0.95rem 1.05rem; cursor: pointer; position: relative; z-index: 1; color: #e8e8ec; transition: transform .13s ease, box-shadow .13s ease, border-color .13s ease; }
              .lt-node:hover { transform: translateY(-2px); }
              .lt-node:disabled { cursor: default; }
              .lt-node--done { background: linear-gradient(180deg, #15241f, #111116); border-color: rgba(52,211,153,0.5); }
              .lt-node--open { border-color: #34d399; animation: lt-pulse 2.8s ease-in-out infinite; }
              .lt-node--locked { background: #0e0e12; border-color: #24242b; color: #6f6f7a; }
              .lt-node--locked:hover { transform: none; }
              /* border glows bright; the background wash stays muted */
              @keyframes lt-pulse {
                0%,100% { box-shadow: 0 0 0 1px rgba(52,211,153,0.28), 0 0 12px rgba(45,212,191,0.10); border-color: #2f9e77; background: linear-gradient(180deg, #14181b, #111116); }
                50%     { box-shadow: 0 0 0 1px rgba(110,231,183,0.7), 0 0 26px rgba(45,212,191,0.32); border-color: #6ee7b7; background: linear-gradient(180deg, rgba(45,212,191,0.13), #121218); }
              }
              @media (prefers-reduced-motion: reduce) { .lt-node--open { animation: none; box-shadow: 0 0 0 1px rgba(52,211,153,0.5); background: linear-gradient(180deg, rgba(45,212,191,0.08), #121218); } }
              .lt-title { font-weight: 650; font-size: 0.98rem; line-height: 1.25; }
              .lt-blurb { font-size: 0.79rem; line-height: 1.35; margin-top: 0.3rem; color: #8f8f9a; }
              .lt-node--locked .lt-blurb { color: #47474f; }
              .lt-needs { font-size: 0.68rem; line-height: 1.3; margin-top: 0.45rem; color: #6a6a75; }
              .lt-dot { position: absolute; top: 0.7rem; right: 0.7rem; width: 7px; height: 7px; border-radius: 50%; background: #34d399; box-shadow: 0 0 6px rgba(52,211,153,0.6); }
            `}</style>
            <svg width={svgSize.w} height={svgSize.h} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 0, overflow: 'visible' }}>
              <defs>
                {[['arrActive', '#6ee7b7']].map(([id, c]) => (
                  <marker key={id} id={id} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={c} /></marker>
                ))}
              </defs>
              {links.map(l => {
                const active = hovered && (l.from === hovered || l.to === hovered)
                const dimmed = hovered && !active
                let d
                if (l.sameCol) {
                  d = `M ${l.x1} ${l.y1} C ${l.x1} ${l.y1 + 24}, ${l.x2} ${l.y2 - 24}, ${l.x2} ${l.y2}`
                } else {
                  const sx = l.x1 + 22, tx = l.x2 - 22, mid = (sx + tx) / 2   // horizontal stubs + flat S through the lane
                  d = `M ${l.x1} ${l.y1} H ${sx} C ${mid} ${l.y1}, ${mid} ${l.y2}, ${tx} ${l.y2} H ${l.x2}`
                }
                // Calm at rest: faint hairlines. On hover: the chain glows, the rest fades away.
                const stroke = active ? (l.done ? '#34d399' : '#6ee7b7') : (l.done ? 'rgba(52,211,153,0.5)' : '#3a3a44')
                const op = active ? 0.95 : dimmed ? 0.04 : (l.done ? 0.5 : 0.14)
                const sw = active ? 2 : 1
                return <path key={l.key} d={d} stroke={stroke} strokeWidth={sw} fill="none" markerEnd={active ? 'url(#arrActive)' : undefined} opacity={op} style={{ transition: 'opacity .15s' }} />
              })}
            </svg>
            {levelsPresent.map(lv => (
              <div key={lv} className="lt-col">
                <div className="lt-era"><b>{lv}</b><span>{LEVEL_NAMES[lv]}</span></div>
                <div className="lt-track">
                  {byLevel[lv].map(t => {
                    const st = stateOf(t)
                    return (
                      <button key={t.id} onClick={() => setSelected(t)} disabled={!!busy}
                        onMouseEnter={() => setHovered(t.slug)} onMouseLeave={() => setHovered(null)}
                        ref={el => { if (el) nodeRefs.current[t.slug] = el }}
                        className={`lt-node lt-node--${st}`}
                        style={{ opacity: neighborSet && !neighborSet.has(t.slug) ? 0.3 : 1, transition: 'opacity .15s' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {st === 'done' && <CheckCircleIcon style={{ fontSize: '1rem', color: '#34d399' }} />}
                          {st === 'locked' && <LockIcon style={{ fontSize: '0.9rem', color: '#5a5a65' }} />}
                          <span className="lt-title">{t.title}</span>
                        </div>
                        {t.blurb && <div className="lt-blurb">{t.blurb}</div>}
                        {t.requires?.length > 0 && st !== 'done' && (
                          <div className="lt-needs">needs: {t.requires.map(r => titleBySlug[r] || r).join(' · ')}</div>
                        )}
                        {t.lessonMeetingId && <span className="lt-dot" title="Lesson ready" />}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            {isOwner && (
              <div style={{ flex: '0 0 auto', paddingTop: '3rem' }}>
                <button onClick={generateCurriculum} disabled={!!busy} title="Reload the canonical curriculum (upserts topics; keeps everyone's progress)" style={{ background: '#141418', border: '1px dashed #3a3a42', color: '#8a8a95', borderRadius: 10, padding: '0.6rem 0.9rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>↻ Reload curriculum</button>
              </div>
            )}
          </div>
        )}
      </main>

      {selected && (() => {
        const st = stateOf(selected)
        return (
          <div onClick={() => setSelected(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#141418', border: '1px solid #2a2a33', borderRadius: 16, padding: '1.4rem', maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ background: 'rgba(52,211,153,0.14)', color: '#6ee7b7', borderRadius: 999, padding: '0.1rem 0.6rem', fontSize: '0.72rem', fontWeight: 600 }}>{selected.level}</span>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{selected.title}</h2>
              </div>
              {selected.blurb && <p style={{ color: '#8a8a95', marginTop: '0.5rem' }}>{selected.blurb}</p>}

              {st === 'locked' ? (
                <div style={{ background: '#101014', border: '1px solid #26262c', borderRadius: 10, padding: '0.8rem', marginTop: '0.8rem', color: '#8a8a95' }}>
                  🔒 Locked. Finish first: <strong style={{ color: '#e8e8ec' }}>{(selected.requires || []).filter(r => !completedSlugs.has(r)).map(r => titleBySlug[r] || r).join(', ')}</strong>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.1rem', flexWrap: 'wrap' }}>
                  <button onClick={() => openTopic(selected)} disabled={!!busy} style={{ background: '#34d399', color: '#06281d', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                    <PlayArrowIcon fontSize="small" /> {selected.lessonMeetingId ? 'Open lesson' : 'Start lesson'}
                  </button>
                  <button onClick={() => openMyPersonal(selected)} disabled={!!busy} title="A private lesson tailored to your profile — only you see it" style={{ background: '#1b1b21', color: '#6ee7b7', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>
                    {readPersonal(selected) ? '✦ Open my version' : '✦ Personalise for me'}
                  </button>
                  {isOwner && selected.lessonMeetingId && <button onClick={() => openTopic(selected, true)} disabled={!!busy} title="Owner: rebuild the shared lesson (replaces it for everyone)" style={{ background: '#1b1b21', color: '#8a8a95', border: '1px solid #2a2a33', borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>↻ Regenerate</button>}
                  {!selected.completed && <button onClick={() => markComplete(selected)} disabled={!!busy} style={{ background: '#1b1b21', color: '#e8e8ec', border: '1px solid #2a2a33', borderRadius: 10, padding: '0.6rem 1.1rem', cursor: 'pointer' }}>Mark complete ✓</button>}
                  {selected.completed && <span style={{ color: '#6ee7b7', alignSelf: 'center' }}>✓ Completed</span>}
                  {getBuiltinLesson(code, selected.slug) && <span title="A hand-authored workbook lesson — opens instantly, no model needed" style={{ alignSelf: 'center', fontSize: '0.78rem', color: '#fbbf24' }}>✦ authored lesson</span>}
                </div>
              )}
              {busy && <div style={{ color: '#6ee7b7', marginTop: '0.8rem', fontSize: '0.85rem' }}>{busy}</div>}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
